/**
 * Form Canvas — one continuous grid of square cells; cards sit on it as
 * movable containers.
 *
 * The grid belongs to the canvas, not to the cards. The canvas holds a
 * 48 / 32 aspect ratio, so one cell is the same size across and down and no
 * pixel height has to be stored anywhere. Cards occupy whole cells of that
 * grid and move through the shared Page Designer helpers (drag, snap, nudge,
 * align, resize). A card's own control grid is that same grid cropped to the
 * card, so a control snaps to the squares the canvas draws. There is no pixel
 * offset, no z-order, and no second coordinate system.
 */
import { useCallback, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { FD } from "@/constants/testIds";
import { cellDeltaFromPixels, cellFromPoint } from "@/lib/designerGrid";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  MIN_CELL_PX,
  cardBounds,
  cardRows,
  controlBinding,
  controlsForCard,
  findCard,
  formBounds,
  withCardGeometry,
} from "@/lib/formDesigner";

export const DRAG_MIME = "application/x-emr-form-item";

const ARROW_KEYS = {
  ArrowUp: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 },
  ArrowLeft: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 },
};
const ARROW_STEP = 1;
const ARROW_STEP_SHIFT = 4;

function isEditableTarget(target) {
  if (!target) return false;
  const tag = typeof target.tagName === "string" ? target.tagName.toUpperCase() : "";
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(target.isContentEditable);
}

function ControlPreview({ control }) {
  const type = control.controlType;
  if (type === "label") {
    return <span className="efd__preview-label">{control.label}</span>;
  }
  if (type === "button") {
    return <span className="efd__preview-button">{control.label}</span>;
  }
  if (type === "checkbox") {
    return (
      <span className="efd__preview-check">
        <span className="efd__preview-box" aria-hidden="true" />
        {control.label}
      </span>
    );
  }
  if (type === "radio") {
    return (
      <span className="efd__preview-check">
        <span className="efd__preview-radio" aria-hidden="true" />
        {control.label}
      </span>
    );
  }
  if (type === "line") {
    return <span className="efd__preview-line" aria-hidden="true" />;
  }
  if (type === "rectangle") {
    return <span className="efd__preview-rect">{control.label}</span>;
  }
  if (type === "image") {
    return <span className="efd__preview-image">{control.label || "Image"}</span>;
  }
  if (type === "tab") {
    return (
      <span className="efd__preview-tabs">
        <span className="efd__preview-tab is-active">Tab 1</span>
        <span className="efd__preview-tab">Tab 2</span>
      </span>
    );
  }
  if (type === "listbox") {
    return (
      <>
        <span className="efd__preview-caption">{control.label}</span>
        <span className="efd__preview-list">
          <span>Item 1</span>
          <span>Item 2</span>
          <span>Item 3</span>
        </span>
      </>
    );
  }
  return (
    <>
      <span className="efd__preview-caption">
        {control.label}
        {control.required ? <span className="efd__preview-required"> *</span> : null}
      </span>
      <span className={`efd__preview-input${type === "textarea" ? " efd__preview-input--tall" : ""}`}>
        {control.placeholder || (type === "date" ? "mm/dd/yyyy" : "")}
        {type === "select" ? <span className="efd__preview-caret">▾</span> : null}
      </span>
    </>
  );
}

function DragDots() {
  return Array.from({ length: 6 }, (_, index) => (
    <span key={index} className="efd__drag-dot" aria-hidden="true" />
  ));
}

/** Both axes are percentages of the canvas, which is what keeps a cell square. */
function cardBoxStyle(card, columns, rows) {
  const geom = withCardGeometry(card, columns);
  return {
    left: `calc((${geom.col} - 1) * (100% / ${columns}))`,
    top: `calc((${geom.row} - 1) * (100% / ${rows}))`,
    width: `calc(${geom.width} * (100% / ${columns}))`,
    height: `calc(${geom.height} * (100% / ${rows}))`,
    "--efd-card-rows": String(geom.height),
  };
}

export default function FormDesignerCanvas({
  form,
  selection,
  selectedCardIds = [],
  onSelect,
  onAddCard,
  onMoveControl,
  onResizeControl,
  onMoveCard,
  onMoveCards,
  onResizeCard,
  onNudgeCards,
  onDeleteCard,
  onDropItem,
  readOnly = false,
}) {
  const surfaceRef = useRef(null);
  const gridRefs = useRef({});
  const controlDragRef = useRef(null);
  const controlResizeRef = useRef(null);
  const cardDragRef = useRef(null);
  const cardResizeRef = useRef(null);
  const moveCardRef = useRef(onMoveCard);
  const moveCardsRef = useRef(onMoveCards);
  const resizeCardRef = useRef(onResizeCard);
  const nudgeRef = useRef(onNudgeCards);
  const selectRef = useRef(onSelect);
  moveCardRef.current = onMoveCard;
  moveCardsRef.current = onMoveCards;
  resizeCardRef.current = onResizeCard;
  nudgeRef.current = onNudgeCards;
  selectRef.current = onSelect;

  const bounds = formBounds(form);
  const columns = bounds.columns || GRID_COLUMNS;
  const rows = bounds.rows || GRID_ROWS;

  const gridRectFor = useCallback(
    (cardId) => {
      const element = gridRefs.current[cardId];
      if (!element) return null;
      const rect = element.getBoundingClientRect();
      const card = findCard(form, cardId);
      const controlRows = cardRows(form, cardId);
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        rows: controlRows,
        columns: card ? cardBounds(card).columns : columns,
      };
    },
    [form, columns],
  );

  function measureCanvas() {
    const rect = surfaceRef.current?.getBoundingClientRect();
    const width = Number(rect?.width) || 0;
    const height = Number(rect?.height) || 0;
    return {
      cellWidth: columns > 0 && width ? width / columns : 0,
      cellHeight: rows > 0 && height ? height / rows : 0,
    };
  }

  useEffect(() => {
    function handleMove(event) {
      const cardDrag = cardDragRef.current;
      if (cardDrag) {
        const delta = cellDeltaFromPixels(
          event.clientX - cardDrag.startX,
          event.clientY - cardDrag.startY,
          cardDrag.cellWidth,
          cardDrag.cellHeight,
        );
        if (cardDrag.positions.length > 1) {
          moveCardsRef.current?.(
            cardDrag.positions.map((position) => ({
              id: position.id,
              col: position.startCol + delta.col,
              row: position.startRow + delta.row,
            })),
          );
        } else {
          const position = cardDrag.positions[0];
          moveCardRef.current?.(position.id, position.startCol + delta.col, position.startRow + delta.row);
        }
        return;
      }
      const cardResize = cardResizeRef.current;
      if (cardResize) {
        const delta = cellDeltaFromPixels(
          event.clientX - cardResize.startX,
          event.clientY - cardResize.startY,
          cardResize.cellWidth,
          cardResize.cellHeight,
        );
        resizeCardRef.current?.(
          cardResize.id,
          cardResize.width + delta.col,
          cardResize.height + delta.row,
        );
        return;
      }
      const drag = controlDragRef.current;
      if (drag) {
        const delta = cellDeltaFromPixels(
          event.clientX - drag.startX,
          event.clientY - drag.startY,
          drag.cellWidth,
          drag.cellHeight,
        );
        onMoveControl(drag.id, drag.col + delta.col, drag.row + delta.row);
        return;
      }
      const resize = controlResizeRef.current;
      if (resize) {
        const delta = cellDeltaFromPixels(
          event.clientX - resize.startX,
          event.clientY - resize.startY,
          resize.cellWidth,
          resize.cellHeight,
        );
        onResizeControl(resize.id, resize.width + delta.col, resize.height + delta.row);
      }
    }
    function handleUp() {
      cardDragRef.current = null;
      cardResizeRef.current = null;
      controlDragRef.current = null;
      controlResizeRef.current = null;
    }
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [onMoveControl, onResizeControl]);

  useEffect(() => {
    if (readOnly || selectedCardIds.length === 0) return undefined;
    function onKeyDown(event) {
      const arrow = ARROW_KEYS[event.key];
      if (!arrow) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      const step = event.shiftKey ? ARROW_STEP_SHIFT : ARROW_STEP;
      nudgeRef.current?.(selectedCardIds, arrow.dx * step, arrow.dy * step);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [readOnly, selectedCardIds]);

  /** A card's cells are canvas cells, measured on the card's own control grid. */
  function measureCard(cardId) {
    const rect = gridRectFor(cardId);
    const card = findCard(form, cardId);
    if (!rect || !card) return { cellWidth: 0, cellHeight: 0 };
    const cardGrid = cardBounds(card);
    return {
      cellWidth: cardGrid.columns > 0 ? rect.width / cardGrid.columns : 0,
      cellHeight: rect.rows > 0 && rect.height ? rect.height / rect.rows : 0,
    };
  }

  function startControlDrag(event, control) {
    onSelect({ kind: "control", id: control.id });
    if (readOnly) return;
    event.stopPropagation();
    controlDragRef.current = {
      id: control.id,
      startX: event.clientX,
      startY: event.clientY,
      col: control.col,
      row: control.row,
      ...measureCard(control.cardId),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function startControlResize(event, control) {
    if (readOnly) return;
    event.stopPropagation();
    onSelect({ kind: "control", id: control.id });
    controlResizeRef.current = {
      id: control.id,
      startX: event.clientX,
      startY: event.clientY,
      width: control.width,
      height: control.height,
      ...measureCard(control.cardId),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  /** Card resize is the Page Designer corner handle, in whole canvas cells. */
  function startCardResize(event, card) {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    selectRef.current({ kind: "card", id: card.id, ids: [card.id] });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const geom = withCardGeometry(card, columns);
    cardResizeRef.current = {
      id: card.id,
      startX: event.clientX,
      startY: event.clientY,
      width: geom.width,
      height: geom.height,
      ...measureCanvas(),
    };
  }

  function startCardDrag(event, card) {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    if (additive) {
      selectRef.current({ kind: "card", id: card.id, additive: true });
      return;
    }
    const groupIds =
      selectedCardIds.length > 1 && selectedCardIds.includes(card.id) ? selectedCardIds : [card.id];
    if (groupIds.length === 1) onSelect({ kind: "card", id: card.id, ids: [card.id] });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const { cellWidth, cellHeight } = measureCanvas();
    cardDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      cellWidth,
      cellHeight,
      positions: groupIds
        .map((id) => (id === card.id ? withCardGeometry(card, columns) : findCard(form, id)))
        .filter(Boolean)
        .map((groupCard) => {
          const geom = withCardGeometry(groupCard, columns);
          return { id: geom.id, startCol: geom.col, startRow: geom.row };
        }),
    };
  }

  function handleDrop(event, card) {
    if (readOnly) return;
    const raw = event.dataTransfer?.getData?.(DRAG_MIME);
    if (!raw) return;
    event.preventDefault();
    let item = null;
    try {
      item = JSON.parse(raw);
    } catch (error) {
      return;
    }
    const rect = gridRectFor(card.id);
    const at = rect
      ? cellFromPoint(rect, { columns: rect.columns, rows: rect.rows }, event.clientX, event.clientY)
      : null;
    onDropItem(card.id, item, at);
  }

  function handleCardPointerDown(event, card) {
    event.stopPropagation();
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    selectRef.current({ kind: "card", id: card.id, additive });
  }

  return (
    <div className="efd__canvas" data-testid={FD.canvas} data-columns={columns} data-rows={rows}>
      <div
        ref={surfaceRef}
        className="efd__canvas-grid"
        data-testid={FD.canvasGrid}
        data-columns={columns}
        data-rows={rows}
        data-min-cell={MIN_CELL_PX}
        style={{
          "--efd-cols": String(columns),
          "--efd-rows": String(rows),
          "--efd-min-cell": `${MIN_CELL_PX}px`,
          aspectRatio: `${columns} / ${rows}`,
        }}
        onPointerDown={() => onSelect(null)}
        onClick={() => onSelect(null)}
      >
        {(form.cards || []).length === 0 ? (
          <div className="efd__empty" data-testid={FD.canvasEmpty}>
            <p className="efd__empty-copy">This form has no cards yet. Add a card, then drop fields or controls into it.</p>
            <button
              type="button"
              className="efd__btn efd__btn--primary"
              data-testid={FD.addCardEmptyBtn}
              disabled={readOnly}
              onClick={(event) => {
                event.stopPropagation();
                onAddCard();
              }}
            >
              + Add Card
            </button>
          </div>
        ) : null}

        {(form.cards || []).map((card) => {
          const geom = withCardGeometry(card, columns);
          const controlRows = cardRows(form, card.id);
          const cardColumns = cardBounds(card).columns;
          const selected = selectedCardIds.includes(card.id);
          return (
            <section
              key={card.id}
              className={`efd__card${selected ? " is-selected" : ""}`}
              data-testid={`${FD.card}-${card.id}`}
              data-card-id={card.id}
              data-columns={cardColumns}
              data-col={geom.col}
              data-row={geom.row}
              data-width={geom.width}
              data-height={geom.height}
              style={cardBoxStyle(card, columns, rows)}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                handleCardPointerDown(event, card);
              }}
            >
              <header
                className="efd__card-head"
                data-testid={`${FD.cardHandle}-${card.id}`}
                onPointerDown={(event) => startCardDrag(event, card)}
                onClick={(event) => event.stopPropagation()}
              >
                <span className="efd__drag-handle" aria-hidden="true">
                  <DragDots />
                </span>
                <span className="efd__card-title" data-testid={`${FD.cardTitle}-${card.id}`}>
                  {card.title}
                </span>
                <span className="efd__card-meta">
                  {geom.width} × {geom.height}
                  {card.collapsible ? " · collapsible" : ""}
                </span>
                {readOnly ? null : (
                  <button
                    type="button"
                    className="efd__card-close"
                    data-testid={`${FD.cardClose}-${card.id}`}
                    aria-label={`Delete ${card.title}`}
                    title={`Delete ${card.title}`}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteCard?.(card.id);
                    }}
                  >
                    <X className="efd__card-close-icon" aria-hidden="true" />
                  </button>
                )}
              </header>
              <div
                className="efd__card-grid"
                data-testid={`${FD.cardGrid}-${card.id}`}
                ref={(element) => {
                  gridRefs.current[card.id] = element;
                }}
                style={{
                  "--efd-cols": String(cardColumns),
                  "--efd-rows": String(controlRows),
                }}
                onDragOver={(event) => {
                  if (!readOnly) event.preventDefault();
                }}
                onDrop={(event) => handleDrop(event, card)}
              >
                {controlsForCard(form, card.id).map((control) => {
                  const isSelected = selection?.kind === "control" && selection.id === control.id;
                  return (
                    <div
                      key={control.id}
                      className={`efd__control efd__control--${control.controlType}${isSelected ? " is-selected" : ""}`}
                      data-testid={`${FD.control}-${control.id}`}
                      data-col={control.col}
                      data-row={control.row}
                      data-width={control.width}
                      data-height={control.height}
                      data-card-id={control.cardId}
                      data-control-type={control.controlType}
                      data-field={control.field || ""}
                      data-binding={controlBinding(form, control)}
                      data-fhir-path={control.binding?.fhirPath || ""}
                      data-field-type={control.binding?.fieldType || ""}
                      data-resource-type={control.binding?.resourceType || ""}
                      style={{
                        gridColumn: `${control.col} / span ${control.width}`,
                        gridRow: `${control.row} / span ${control.height}`,
                      }}
                      onPointerDown={(event) => startControlDrag(event, control)}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <ControlPreview control={control} />
                      {readOnly ? null : (
                        <span
                          className="efd__control-handle"
                          role="presentation"
                          data-testid={`${FD.controlResize}-${control.id}`}
                          onPointerDown={(event) => startControlResize(event, control)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {readOnly ? null : (
                <span
                  className="efd__card-handle"
                  role="presentation"
                  data-testid={`${FD.cardResize}-${card.id}`}
                  title={`Resize ${card.title}`}
                  onPointerDown={(event) => startCardResize(event, card)}
                />
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
